/**
 * Email Campaign Widget
 * Shows email campaign enrollment and engagement for CRM contacts
 */

import React, { useState, useEffect } from 'react';
import { Mail, TrendingUp, Eye, MousePointerClick, MessageCircle, BarChart3, Plus, X } from 'lucide-react';
import crmEmailBridgeService, { 
  EmailCampaignEnrollment, 
  EmailEngagementStats 
} from '../../services/crm/crmEmailBridgeService';
import { toast } from 'react-hot-toast';

interface EmailCampaignWidgetProps {
  contactId: string;
  organizationId: string;
  contactEmail?: string;
}

const EmailCampaignWidget: React.FC<EmailCampaignWidgetProps> = ({
  contactId,
  organizationId,
  contactEmail
}) => {
  const [enrollments, setEnrollments] = useState<EmailCampaignEnrollment[]>([]);
  const [stats, setStats] = useState<EmailEngagementStats | null>(null);
  const [availableCampaigns, setAvailableCampaigns] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<string>('');
  const [isEnrolling, setIsEnrolling] = useState(false);

  useEffect(() => {
    loadData();
  }, [contactId, organizationId]);

  const loadData = async () => {
    if (!contactEmail) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      
      // Load enrollments and stats in parallel
      const [enrollmentsData, statsData] = await Promise.all([
        crmEmailBridgeService.getContactEnrollments(contactId, organizationId),
        crmEmailBridgeService.getEngagementStats(contactId, organizationId)
      ]);

      setEnrollments(enrollmentsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading email campaign data:', error);
      toast.error('Failed to load email campaign data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAvailableCampaigns = async () => {
    try {
      const campaigns = await crmEmailBridgeService.getAvailableCampaigns();
      setAvailableCampaigns(campaigns);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      toast.error('Failed to load campaigns');
    }
  };

  const handleEnrollClick = () => {
    setShowEnrollModal(true);
    loadAvailableCampaigns();
  };

  const handleEnroll = async () => {
    if (!selectedCampaign) {
      toast.error('Please select a campaign');
      return;
    }

    try {
      setIsEnrolling(true);
      
      await crmEmailBridgeService.enrollInCampaign({
        crm_contact_id: contactId,
        email_campaign_id: selectedCampaign,
        organization_id: organizationId,
        source: 'manual'
      });

      toast.success('Contact enrolled in campaign');
      setShowEnrollModal(false);
      setSelectedCampaign('');
      loadData(); // Reload data
    } catch (error: any) {
      console.error('Error enrolling in campaign:', error);
      toast.error(error.message || 'Failed to enroll in campaign');
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleUnenroll = async (enrollmentId: string) => {
    if (!window.confirm('Are you sure you want to unenroll this contact from the campaign?')) {
      return;
    }

    try {
      await crmEmailBridgeService.unenrollFromCampaign(enrollmentId, organizationId);
      toast.success('Contact unenrolled from campaign');
      loadData();
    } catch (error) {
      console.error('Error unenrolling:', error);
      toast.error('Failed to unenroll from campaign');
    }
  };

  if (!contactEmail) {
    return (
      <div className="rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 p-6 ring-1 ring-gray-200">
        <div className="flex items-center gap-3 mb-3">
          <Mail className="w-5 h-5 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900">Email Campaigns</h3>
        </div>
        <p className="text-sm text-gray-500">
          Add an email address to this contact to enable email campaign enrollment.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="rounded-2xl bg-gradient-to-br from-blue-50 to-purple-50 p-6 ring-1 ring-blue-200">
        <div className="animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-2xl bg-gradient-to-br from-blue-50 to-purple-50 p-6 ring-1 ring-blue-200">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Email Campaigns</h3>
          </div>
          <button
            onClick={handleEnrollClick}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Enroll in Campaign
          </button>
        </div>

        {/* Engagement Stats */}
        {stats && (
          <div className="grid grid-cols-4 gap-3 mb-4">
            <div className="bg-white rounded-lg p-3 ring-1 ring-gray-200">
              <div className="flex items-center gap-2 mb-1">
                <Mail className="w-4 h-4 text-gray-500" />
                <span className="text-xs text-gray-600">Sent</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">{stats.total_sent}</div>
            </div>

            <div className="bg-white rounded-lg p-3 ring-1 ring-gray-200">
              <div className="flex items-center gap-2 mb-1">
                <Eye className="w-4 h-4 text-blue-500" />
                <span className="text-xs text-gray-600">Opens</span>
              </div>
              <div className="text-2xl font-bold text-blue-600">{stats.total_opened}</div>
              <div className="text-xs text-gray-500">{stats.open_rate.toFixed(1)}%</div>
            </div>

            <div className="bg-white rounded-lg p-3 ring-1 ring-gray-200">
              <div className="flex items-center gap-2 mb-1">
                <MessageCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-xs text-gray-600">Replies</span>
              </div>
              <div className="text-2xl font-bold text-emerald-600">{stats.total_replied}</div>
              <div className="text-xs text-gray-500">{stats.reply_rate.toFixed(1)}%</div>
            </div>

            <div className="bg-white rounded-lg p-3 ring-1 ring-gray-200">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-purple-500" />
                <span className="text-xs text-gray-600">Score</span>
              </div>
              <div className="text-2xl font-bold text-purple-600">{stats.engagement_score}</div>
              <div className="text-xs text-gray-500">/100</div>
            </div>
          </div>
        )}

        {/* Active Enrollments */}
        {enrollments.length > 0 ? (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Active Campaigns ({enrollments.length})</h4>
            {enrollments.map((enrollment) => (
              <div
                key={enrollment.id}
                className="bg-white rounded-lg p-3 ring-1 ring-gray-200 flex items-center justify-between"
              >
                <div className="flex-1">
                  <div className="font-medium text-gray-900 text-sm">
                    {enrollment.metadata?.campaign_name || 'Campaign'}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Enrolled {new Date(enrollment.enrolled_at).toLocaleDateString()} • 
                    {enrollment.source === 'workflow' && ' via workflow'}
                    {enrollment.source === 'auto' && ' automatically'}
                    {enrollment.source === 'manual' && ' manually'}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    enrollment.status === 'active' ? 'bg-green-100 text-green-800' :
                    enrollment.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                    enrollment.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {enrollment.status}
                  </span>
                  <button
                    onClick={() => handleUnenroll(enrollment.id)}
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                    title="Unenroll from campaign"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 bg-white rounded-lg ring-1 ring-gray-200">
            <Mail className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Not enrolled in any campaigns</p>
            <button
              onClick={handleEnrollClick}
              className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Enroll in a campaign →
            </button>
          </div>
        )}

        {/* Last Activity */}
        {stats?.last_engagement && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <BarChart3 className="w-4 h-4" />
              <span>Last activity: {new Date(stats.last_engagement).toLocaleDateString()}</span>
            </div>
          </div>
        )}
      </div>

      {/* Enrollment Modal */}
      {showEnrollModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Enroll in Campaign</h3>
              <button
                onClick={() => setShowEnrollModal(false)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Campaign
              </label>
              <select
                value={selectedCampaign}
                onChange={(e) => setSelectedCampaign(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Choose a campaign...</option>
                {availableCampaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name} ({campaign.status})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowEnrollModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                disabled={isEnrolling}
              >
                Cancel
              </button>
              <button
                onClick={handleEnroll}
                disabled={!selectedCampaign || isEnrolling}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isEnrolling ? 'Enrolling...' : 'Enroll'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmailCampaignWidget;
